/**
 * Centralized Logger Utility
 * Replaces all console.log statements for production safety
 */

const IS_PRODUCTION = import.meta.env.MODE === 'production';
const IS_DEV = import.meta.env.MODE === 'development';

class Logger {
  /**
   * Debug level - development only
   * Use for detailed debugging information
   */
  debug(...args) {
    if (IS_DEV) {
      console.log('[DEBUG]', new Date().toISOString(), ...args);
    }
  }

  /**
   * Info level - development only
   * Use for general information
   */
  info(...args) {
    if (IS_DEV) {
      console.log('[INFO]', new Date().toISOString(), ...args);
    }
  }

  /**
   * Warning level - all environments
   * Use for recoverable errors or important notices
   */
  warn(...args) {
    console.warn('[WARN]', new Date().toISOString(), ...args);
    
    if (IS_PRODUCTION) {
      this._sendToMonitoring('warn', args);
    }
  }

  /**
   * Error level - all environments
   * Use for errors that need attention
   */
  error(...args) {
    console.error('[ERROR]', new Date().toISOString(), ...args);
    
    if (IS_PRODUCTION) {
      this._sendToMonitoring('error', args);
    }
  }

  /**
   * Sensitive data - NEVER logs in production
   * Use only for debugging auth tokens, passwords, etc.
   */
  sensitive(...args) {
    if (IS_DEV && import.meta.env.VITE_LOG_SENSITIVE === 'true') {
      console.log('[SENSITIVE]', new Date().toISOString(), ...args);
    }
    // Completely silent in production
  }

  /**
   * Performance timing
   * Measures execution time of functions
   */
  time(label) {
    if (IS_DEV) {
      console.time(`[TIME] ${label}`);
    }
  }

  timeEnd(label) {
    if (IS_DEV) {
      console.timeEnd(`[TIME] ${label}`);
    }
  }

  /**
   * Group related logs
   */
  group(label, collapsed = false) {
    if (IS_DEV) {
      collapsed ? console.groupCollapsed(label) : console.group(label);
    }
  }

  groupEnd() {
    if (IS_DEV) {
      console.groupEnd();
    }
  }

  /**
   * Table display for arrays/objects
   */
  table(data) {
    if (IS_DEV) {
      console.table(data);
    }
  }

  /**
   * Send errors to monitoring service
   * @private
   */
  _sendToMonitoring(level, args) {
    // TODO: Integrate with Sentry, LogRocket, or similar
    // Example:
    // if (window.Sentry) {
    //   Sentry.captureMessage(args.join(' '), level);
    // }
  }

  /**
   * Assert condition
   */
  assert(condition, message) {
    if (IS_DEV && !condition) {
      console.assert(condition, message);
    }
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;

// Named exports for convenience
export const {
  debug,
  info,
  warn,
  error,
  sensitive,
  time,
  timeEnd,
  group,
  groupEnd,
  table,
  assert
} = logger;
